import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Plus, Pencil, Trash2, X, AlertCircle, ChevronRight,
  FlaskConical, Layers, Leaf, Settings2, Search, GripVertical, Power,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import type {
  AppBoom, AppPlanEtapa, AppPlanCiclo, AppPlanProducto,
} from '../../types/planManejo';
import type { CicloCosecha } from '../../types/planManejo';
import {
  inputCls, selectCls, labelCls, btnPrimary, btnSecondary, btnDanger,
  errorCls, pageTitleCls, pageSubtitleCls, sectionLabelCls, cardCls,
} from '../../components/ui';

// ─── Constantes ───────────────────────────────────────────────────────────────

const CICLO_COSECHA_LABEL: Record<CicloCosecha, string> = {
  PC: 'Primera Cosecha', RC: 'Segunda Cosecha', RC2: 'Tercera Cosecha',
  SEMILLERO: 'Semillero', BARBECHO: 'Barbecho',
};

const CICLO_COLOR: Record<CicloCosecha, string> = {
  PC: 'var(--tone-success-text)', RC: 'var(--tone-info-text)',
  RC2: 'var(--tone-purple-text)', SEMILLERO: 'var(--tone-warn-text)',
  BARBECHO: 'var(--ink-faint)',
};

function fmtCRC(n: number | null | undefined): string {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return '₡' + v.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Grid del panel de productos: manija, #, artíc, nombre, depto, dosis, medida,
// reingreso, costo unit., costo/ha, acciones.
const GRID_COLS = '1.5rem 2.5rem 3rem 1fr 5.5rem 3.5rem 3rem 3.5rem 5.5rem 6rem 2rem';

const MEDIDAS = ['Kil', 'Lt', 'Kg', 'Caj', 'Gr', 'LT'];
const GRUPOS_QUIMICOS = ['Fertilizante', 'Herbicida', 'Fungicida', 'Insecticida', 'Foliar', 'Inductor', 'Otro'];
const DEPARTAMENTOS = ['FERTILIZANTES', 'AGROQUIMICOS', 'INDUCTORES', 'OTRO'];

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide = false }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="relative w-full flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{
          maxWidth: wide ? '56rem' : '36rem', maxHeight: '92vh',
          background: 'var(--surface-overlay)', border: '1px solid var(--line)',
        }}>
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{title}</span>
          <button onClick={onClose} style={{ color: 'var(--ink-faint)' }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Formulario Boom ──────────────────────────────────────────────────────────

function BoomForm({ initial, onSave, onCancel }: {
  initial?: AppBoom; onSave: (d: Partial<AppBoom>) => Promise<void>; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    codigo: initial?.codigo ?? '', nombre: initial?.nombre ?? '',
    capacidad_l: initial?.capacidad_l ?? '' as any,
    ancho_m: initial?.ancho_m ?? '' as any,
    descripcion: initial?.descripcion ?? '',
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.codigo.trim()) { setErr('El código es obligatorio.'); return; }
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (!form.capacidad_l || Number(form.capacidad_l) <= 0) { setErr('La capacidad debe ser mayor a 0.'); return; }
    if (form.ancho_m !== '' && Number(form.ancho_m) <= 0) { setErr('El ancho debe ser mayor a 0.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        ...form,
        capacidad_l: Number(form.capacidad_l),
        ancho_m: form.ancho_m === '' ? undefined : Number(form.ancho_m),
      });
    }
    catch (ex: any) { setErr(ex?.message ?? 'Error al guardar.'); setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Código *</label>
          <input className={inputCls} value={form.codigo} onChange={e => set('codigo', e.target.value)} placeholder="01" /></div>
        <div><label className={labelCls}>Nombre *</label>
          <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Boom Grande" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Capacidad (litros) *</label>
          <input className={inputCls} type="number" step="0.01" min="0.01" value={form.capacidad_l}
            onChange={e => set('capacidad_l', e.target.value)} placeholder="6000" /></div>
        <div><label className={labelCls}>Ancho de aspersión (m)</label>
          <input className={inputCls} type="number" step="0.1" min="0.1" value={form.ancho_m}
            onChange={e => set('ancho_m', e.target.value)} placeholder="4.0" />
          <p className="text-[10px] mt-1" style={{ color: 'var(--ink-faint)' }}>Usado para calcular cobertura del recorrido sobre el lote.</p></div>
      </div>
      <div><label className={labelCls}>Descripción</label>
        <input className={inputCls} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} /></div>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Guardando...' : initial ? 'Actualizar' : 'Crear Boom'}</button>
      </div>
    </form>
  );
}

// ─── Formulario Etapa ─────────────────────────────────────────────────────────

function EtapaForm({ initial, onSave, onCancel }: {
  initial?: AppPlanEtapa; onSave: (d: Partial<AppPlanEtapa>) => Promise<void>; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    nombre: initial?.nombre ?? '',
    ciclo_cosecha: (initial?.ciclo_cosecha ?? 'PC') as CicloCosecha,
    orden: initial?.orden ?? 0,
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    setSaving(true); setErr('');
    try { await onSave({ ...form, nombre: form.nombre.trim() }); }
    catch (ex: any) { setErr(ex?.message ?? 'Error al guardar.'); setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div><label className={labelCls}>Nombre de la Etapa *</label>
        <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)}
          placeholder="Ej: Desarrollo PC, Post Forza RC" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Cosecha</label>
          <select className={selectCls} value={form.ciclo_cosecha} onChange={e => set('ciclo_cosecha', e.target.value as CicloCosecha)}>
            {(Object.entries(CICLO_COSECHA_LABEL) as [CicloCosecha, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select></div>
        <div><label className={labelCls}>Orden</label>
          <input className={inputCls} type="number" min="0" value={form.orden}
            onChange={e => set('orden', parseInt(e.target.value) || 0)} /></div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} />
        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Etapa activa (aparece en generación de boletas)</span>
      </label>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Guardando...' : initial ? 'Actualizar' : 'Crear Etapa'}</button>
      </div>
    </form>
  );
}

// ─── Formulario Ciclo ─────────────────────────────────────────────────────────

function CicloForm({ etapaId, booms, initial, onSave, onCancel }: {
  etapaId: number; booms: AppBoom[];
  initial?: AppPlanCiclo; onSave: (d: Partial<AppPlanCiclo>) => Promise<void>; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    nombre: initial?.nombre ?? '',
    dias: initial?.dias ?? '' as any,
    boom_id: initial?.boom_id ?? (booms[0]?.id ?? '') as any,
    agua_l: initial?.agua_l ?? '' as any,
    grupo_quimico: initial?.grupo_quimico ?? '',
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  // El agua por boom es el volumen de referencia de la fórmula: la dosis/ha se
  // disuelve en `agua_l` litros/ha. Si cambia, los productos del ciclo deben
  // seguirla (ver aviso y propagación en saveCiclo).
  const aguaCambia =
    !!initial && form.agua_l !== '' &&
    Number(form.agua_l) !== Number(initial.agua_l ?? NaN);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (!form.dias || Number(form.dias) < 1) { setErr('Los días son obligatorios.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        ...form, etapa_id: etapaId,
        nombre: form.nombre.trim(),
        dias: Number(form.dias),
        boom_id: form.boom_id ? Number(form.boom_id) : undefined,
        agua_l: form.agua_l ? Number(form.agua_l) : undefined,
      });
    } catch (ex: any) { setErr(ex?.message ?? 'Error al guardar.'); setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Nombre del Ciclo *</label>
          <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)}
            placeholder="Plag frut 1, Foliar 19 pc" /></div>
        <div><label className={labelCls}>Días desde Siembra *</label>
          <input className={inputCls} type="number" min="1" value={form.dias}
            onChange={e => set('dias', e.target.value)} placeholder="14, 28, 56..." /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Boom (equipo)</label>
          <select className={selectCls} value={form.boom_id} onChange={e => set('boom_id', e.target.value)}>
            <option value="">— Sin boom —</option>
            {booms.map(b => <option key={b.id} value={b.id}>{b.codigo} — {b.nombre} ({b.capacidad_l.toLocaleString('es-CR')} Lts)</option>)}
          </select></div>
        <div><label className={labelCls}>Agua por Boom (lts)</label>
          <input className={inputCls} type="number" step="0.01" min="0" value={form.agua_l}
            onChange={e => set('agua_l', e.target.value)} placeholder="2000" /></div>
      </div>
      {aguaCambia && (
        <div className="flex gap-2 px-3 py-2 rounded text-[11px]"
          style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
          <AlertCircle size={13} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
          <span>
            Al guardar, el <strong>volumen de referencia</strong> de todos los productos de este ciclo
            pasa de {Number(initial!.agua_l ?? 0).toLocaleString('es-CR')} a{' '}
            <strong>{Number(form.agua_l).toLocaleString('es-CR')} lts</strong>, para que la cantidad por
            boom siga cuadrando con la dosis por hectárea.
          </span>
        </div>
      )}
      <div><label className={labelCls}>Grupo Químico</label>
        <select className={selectCls} value={form.grupo_quimico} onChange={e => set('grupo_quimico', e.target.value)}>
          <option value="">— Sin grupo —</option>
          {GRUPOS_QUIMICOS.map(g => <option key={g} value={g}>{g}</option>)}
        </select></div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} />
        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Ciclo activo</span>
      </label>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Guardando...' : initial ? 'Actualizar' : 'Agregar Ciclo'}</button>
      </div>
    </form>
  );
}

// ─── Buscador de artículos ERP ───────────────────────────────────────────────

interface InvProducto {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  departamento?: string;
  unidad_medida?: string;
}

function ArticuloBuscador({ empresaId, value, nombre, onSelect }: {
  empresaId: number;
  value: string;
  nombre: string;
  onSelect: (p: InvProducto) => void;
}) {
  const [query, setQuery] = useState(value ? `${value} — ${nombre}` : '');
  const [resultados, setResultados] = useState<InvProducto[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value && nombre && query === '') setQuery(`${value} — ${nombre}`);
  }, [value, nombre]); // eslint-disable-line

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setAbierto(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function buscar(q: string) {
    setQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) { setResultados([]); setAbierto(false); return; }
    timerRef.current = setTimeout(async () => {
      setBuscando(true);
      const { data: bodegaFinca } = await supabase
        .from('inv_bodegas')
        .select('id')
        .eq('empresa_id', empresaId)
        .ilike('nombre', 'Finca')
        .eq('activo', true)
        .maybeSingle();
      let productosQuery = supabase
        .from('inv_productos')
        .select('id, codigo, descripcion, descripcion_detallada, nombre_app, nombre_boleta, departamento, unidad_medida')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .or(`descripcion.ilike.%${q}%,descripcion_detallada.ilike.%${q}%,nombre_app.ilike.%${q}%,nombre_boleta.ilike.%${q}%,codigo.ilike.%${q}%`)
        .order('descripcion')
        .limit(12);
      productosQuery = bodegaFinca?.id
        ? productosQuery.or(`bodega_id.eq.${bodegaFinca.id},vfp_bodega.eq.1`)
        : productosQuery.eq('vfp_bodega', '1');
      const { data } = await productosQuery;
      setResultados((data ?? []).map((p: any) => ({
        id: p.id,
        codigo: p.codigo,
        nombre: p.nombre_app || p.nombre_boleta || p.descripcion || p.descripcion_detallada || p.codigo,
        descripcion: p.descripcion_detallada || p.descripcion,
        departamento: p.departamento,
        unidad_medida: p.unidad_medida,
      })));
      setAbierto(true);
      setBuscando(false);
    }, 280);
  }

  function seleccionar(p: InvProducto) {
    setQuery(`${p.codigo} — ${p.nombre}`);
    setResultados([]);
    setAbierto(false);
    onSelect(p);
  }

  return (
    <div ref={wrapRef} className="relative">
      <label className={labelCls}>Buscar Artículo ERP *</label>
      <div className="relative">
        <input
          className={inputCls}
          value={query}
          onChange={e => buscar(e.target.value)}
          onFocus={() => { if (resultados.length > 0) setAbierto(true); }}
          placeholder="Buscar por código o nombre..."
        />
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
          {buscando
            ? <div className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--emp-accent)' }} />
            : <Search size={13} style={{ color: 'var(--ink-faint)' }} />}
        </div>
      </div>
      {abierto && resultados.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-lg shadow-xl overflow-hidden"
          style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line)', maxHeight: '220px', overflowY: 'auto' }}>
          {resultados.map(p => (
            <button key={p.id} type="button"
              className="w-full text-left px-3 py-2 flex gap-3 items-start transition-colors"
              style={{ borderBottom: '1px solid var(--line-dim)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-deep)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              onClick={() => seleccionar(p)}>
              <span className="font-mono text-[11px] font-bold shrink-0 pt-0.5" style={{ color: 'var(--emp-accent-txt)', minWidth: '3rem' }}>{p.codigo}</span>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: 'var(--ink)' }}>{p.nombre}</p>
                {(p.descripcion || p.departamento) && (
                  <p className="text-[10px] truncate" style={{ color: 'var(--ink-faint)' }}>
                    {[p.descripcion, p.departamento].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Formulario Producto ──────────────────────────────────────────────────────

function ProductoForm({ cicloId, empresaId, aguaCiclo, initial, onSave, onCancel }: {
  cicloId: number; empresaId: number; aguaCiclo?: number; initial?: AppPlanProducto;
  onSave: (d: Partial<AppPlanProducto>) => Promise<void>; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    inv_producto_id: initial?.inv_producto_id ?? null as number | null,
    articulo: initial?.articulo ?? '',
    nombre_producto: initial?.nombre_producto ?? '',
    descripcion: initial?.descripcion ?? '',
    grupo_quimico: initial?.grupo_quimico ?? '',
    ingrediente_activo: initial?.ingrediente_activo ?? '',
    departamento: initial?.departamento ?? '',
    dosis: initial?.dosis ?? '' as any,
    // El volumen de referencia por defecto es el agua por boom del ciclo.
    volumen: (initial?.volumen ?? aguaCiclo ?? '') as any,
    medida: initial?.medida ?? 'Kil',
    latencia: initial?.latencia ?? '' as any,
    reingreso: initial?.reingreso ?? 'N/A',
    orden: initial?.orden ?? 0,
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  function handleArticuloSelect(p: InvProducto) {
    setForm(f => ({
      ...f,
      inv_producto_id: p.id,
      articulo: p.codigo,
      nombre_producto: p.nombre,
      descripcion: p.descripcion ?? f.descripcion,
      departamento: p.departamento ?? f.departamento,
      medida: p.unidad_medida ?? f.medida,
    }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.articulo.trim()) { setErr('El código de artículo es obligatorio.'); return; }
    if (!form.nombre_producto.trim()) { setErr('El nombre del producto es obligatorio.'); return; }
    if (!form.dosis || Number(form.dosis) <= 0) { setErr('La dosis debe ser mayor a 0.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        ...form, ciclo_id: cicloId,
        articulo: form.articulo.trim(),
        nombre_producto: form.nombre_producto.trim(),
        dosis: Number(form.dosis),
        volumen: form.volumen ? Number(form.volumen) : undefined,
        latencia: form.latencia ? Number(form.latencia) : undefined,
        orden: Number(form.orden),
      });
    } catch (ex: any) { setErr(ex?.message ?? 'Error al guardar.'); setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className={sectionLabelCls}>Identificación del Producto</p>

      <ArticuloBuscador
        empresaId={empresaId}
        value={form.articulo}
        nombre={form.nombre_producto}
        onSelect={handleArticuloSelect}
      />

      {form.articulo && (
        <div className="flex items-center gap-2 px-3 py-2 rounded text-xs"
          style={{ background: 'var(--emp-accent-bg)', border: '1px solid var(--emp-accent)', color: 'var(--emp-accent-txt)' }}>
          <span className="font-mono font-bold">{form.articulo}</span>
          <span className="opacity-60">·</span>
          <span className="flex-1 truncate">{form.nombre_producto}</span>
        </div>
      )}

      <div><label className={labelCls}>Descripción / Ingrediente Activo</label>
        <input className={inputCls} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="13% CALCIO + 8%BORO" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={labelCls}>Grupo Químico</label>
          <select className={selectCls} value={form.grupo_quimico} onChange={e => set('grupo_quimico', e.target.value)}>
            <option value="">—</option>
            {GRUPOS_QUIMICOS.map(g => <option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className={labelCls}>Departamento</label>
          <select className={selectCls} value={form.departamento} onChange={e => set('departamento', e.target.value)}>
            <option value="">—</option>
            {DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
          </select></div>
      </div>

      <p className={sectionLabelCls}>Fórmula de Aplicación</p>
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-2"><label className={labelCls}>Dosis (por ha) *</label>
          <input className={inputCls} type="number" step="0.001" min="0.001" value={form.dosis}
            onChange={e => set('dosis', e.target.value)} placeholder="5.000" /></div>
        <div><label className={labelCls}>Medida</label>
          <select className={selectCls} value={form.medida} onChange={e => set('medida', e.target.value)}>
            {MEDIDAS.map(m => <option key={m} value={m}>{m}</option>)}
          </select></div>
        <div><label className={labelCls}>Volumen Boom ref.</label>
          <input className={inputCls} type="number" step="0.01" min="0" value={form.volumen}
            onChange={e => set('volumen', e.target.value)} placeholder={String(aguaCiclo ?? 2000)} />
          {aguaCiclo != null && form.volumen !== '' && Number(form.volumen) !== Number(aguaCiclo) && (
            <button type="button"
              className="text-[10px] mt-1 underline"
              style={{ color: 'var(--tone-warn-text)' }}
              onClick={() => set('volumen', aguaCiclo)}>
              ≠ agua del ciclo ({aguaCiclo.toLocaleString('es-CR')} lts) — igualar
            </button>
          )}</div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><label className={labelCls}>Latencia (días)</label>
          <input className={inputCls} type="number" min="0" value={form.latencia}
            onChange={e => set('latencia', e.target.value)} placeholder="0" /></div>
        <div><label className={labelCls}>Reingreso</label>
          <input className={inputCls} value={form.reingreso} onChange={e => set('reingreso', e.target.value)} placeholder="N/A, 24h, 48h" /></div>
        <div><label className={labelCls}>Orden</label>
          <input className={inputCls} type="number" min="0" value={form.orden}
            onChange={e => set('orden', parseInt(e.target.value) || 0)} /></div>
      </div>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Guardando...' : initial ? 'Actualizar' : 'Agregar Producto'}</button>
      </div>
    </form>
  );
}

// ─── Panel de Productos (panel derecho) ──────────────────────────────────────

// Costo por producto del ciclo (desde la vista v_app_plan_costos).
interface CostoFila { costo_unitario: number; costo_ha: number; sin_costo: boolean }

function PanelProductos({ ciclo, empresaId }: { ciclo: AppPlanCiclo; empresaId: number }) {
  const [productos, setProductos] = useState<AppPlanProducto[]>([]);
  const [costos, setCostos] = useState<Map<number, CostoFila>>(new Map());
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | AppPlanProducto | null>(null);
  const [deleting, setDeleting] = useState<AppPlanProducto | null>(null);
  // Drag & drop: id del producto que se está arrastrando
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [hoverId, setHoverId] = useState<number | null>(null);

  const [igualando, setIgualando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    const [prodRes, costoRes] = await Promise.all([
      supabase
        .from('app_plan_productos')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('ciclo_id', ciclo.id)
        .eq('activo', true)
        .order('orden')
        .order('nombre_producto'),
      // Costo/ha de cada producto del ciclo (JOIN a inv_productos vía la vista)
      supabase
        .from('v_app_plan_costos')
        .select('producto_id, costo_unitario, costo_ha, sin_costo')
        .eq('empresa_id', empresaId)
        .eq('ciclo_id', ciclo.id),
    ]);
    setProductos(prodRes.data ?? []);
    const m = new Map<number, CostoFila>();
    (costoRes.data ?? []).forEach((c: any) => m.set(c.producto_id, {
      costo_unitario: Number(c.costo_unitario) || 0,
      costo_ha: Number(c.costo_ha) || 0,
      sin_costo: !!c.sin_costo,
    }));
    setCostos(m);
    setLoading(false);
  }, [empresaId, ciclo.id]);

  useEffect(() => { cargar(); }, [cargar]);
  // Al cambiar el agua del ciclo los volúmenes se reescriben en BD (saveCiclo)
  // y este panel debe releer para reflejar los nuevos valores.
  useEffect(() => { cargar(); }, [ciclo.agua_l]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCicloHa = productos.reduce((s, p) => s + (costos.get(p.id)?.costo_ha ?? 0), 0);
  const nSinCosto = productos.filter(p => costos.get(p.id)?.sin_costo).length;

  // Volumen de referencia desalineado del agua del ciclo: la cantidad por boom
  // se calcula (dosis / volumen) × capacidad, así que si volumen ≠ agua_l la
  // suma de los booms no da dosis × área (sobre o sub-dosificación en campo).
  const aguaCiclo = ciclo.agua_l != null ? Number(ciclo.agua_l) : null;
  const desalineados = aguaCiclo
    ? productos.filter(p => p.volumen == null || Number(p.volumen) !== aguaCiclo)
    : [];

  async function igualarVolumenes() {
    if (!aguaCiclo) return;
    setIgualando(true);
    await supabase.from('app_plan_productos')
      .update({ volumen: aguaCiclo })
      .eq('empresa_id', empresaId)
      .eq('ciclo_id', ciclo.id);
    await cargar();
    setIgualando(false);
  }

  // Reordenar productos: mueve `draggedId` justo antes de `targetId`.
  // Asigna nuevos órdenes como múltiplos de 10 (10, 20, 30, …) para
  // facilitar inserciones futuras y persiste en BD.
  async function moverProducto(draggedId: number, targetId: number) {
    if (draggedId === targetId) return;
    const lista = [...productos];
    const fromIdx = lista.findIndex(p => p.id === draggedId);
    const toIdx = lista.findIndex(p => p.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [movido] = lista.splice(fromIdx, 1);
    lista.splice(toIdx, 0, movido);
    // Reasignar orden en múltiplos de 10
    const reordenados = lista.map((p, idx) => ({ ...p, orden: (idx + 1) * 10 }));
    // Optimistic UI
    setProductos(reordenados);
    // Persistir: solo los que cambiaron de orden
    const cambios = reordenados
      .map((p, idx) => ({ p, orden_nuevo: (idx + 1) * 10 }))
      .filter(({ p, orden_nuevo }) => {
        const orig = productos.find(o => o.id === p.id);
        return orig?.orden !== orden_nuevo;
      });
    try {
      await Promise.all(cambios.map(({ p, orden_nuevo }) =>
        supabase.from('app_plan_productos').update({ orden: orden_nuevo }).eq('id', p.id)
      ));
    } catch (e) {
      // Rollback si falla: recargar
      await cargar();
    }
  }

  async function save(data: Partial<AppPlanProducto>) {
    if (modal && modal !== 'create') {
      await supabase.from('app_plan_productos').update(data).eq('id', (modal as AppPlanProducto).id);
    } else {
      await supabase.from('app_plan_productos').insert({ ...data, empresa_id: empresaId, ciclo_id: ciclo.id });
    }
    setModal(null);
    await cargar();
  }

  async function confirmDelete() {
    if (!deleting) return;
    await supabase.from('app_plan_productos').update({ activo: false }).eq('id', deleting.id);
    setDeleting(null);
    await cargar();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>
            Productos — {ciclo.nombre}
          </p>
          <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>
            Día {ciclo.dias} · {ciclo.agua_l?.toLocaleString('es-CR')} lts agua
          </p>
        </div>
        <button className={btnPrimary} onClick={() => setModal('create')}>
          <span className="flex items-center gap-1"><Plus size={13} /> Producto</span>
        </button>
      </div>

      {!loading && desalineados.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 mb-2 rounded shrink-0"
          style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
          <AlertCircle size={13} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 2 }} />
          <div className="flex-1 min-w-0">
            <p className="text-[11px]" style={{ color: 'var(--tone-warn-ink)' }}>
              {desalineados.length} producto{desalineados.length > 1 ? 's' : ''} con volumen de referencia
              distinto al agua del ciclo ({aguaCiclo?.toLocaleString('es-CR')} lts):{' '}
              {desalineados.slice(0, 3).map(p => p.articulo).join(', ')}
              {desalineados.length > 3 ? '…' : ''}. La cantidad por boom queda mal calculada.
            </p>
          </div>
          <button className={btnSecondary} disabled={igualando} onClick={igualarVolumenes}>
            {igualando ? 'Igualando…' : 'Igualar volúmenes'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--emp-accent)' }} />
        </div>
      ) : productos.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 opacity-50">
          <FlaskConical size={28} style={{ color: 'var(--ink-faint)' }} />
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin productos en este ciclo</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {/* Cabecera tabla — agregamos columna 1.5rem para la manija de drag */}
          <div className="grid gap-1 px-2 pb-1" style={{
            gridTemplateColumns: GRID_COLS,
            borderBottom: '1px solid var(--line)',
          }}>
            {['', '#', 'Artíc.', 'Nombre / Ingrediente', 'Depto.', 'Dosis', 'Med.', 'Reing.', 'Costo unit.', 'Costo/ha', ''].map((h, i) => (
              <span key={i} className="text-[10px] font-bold uppercase tracking-wide"
                style={{ color: 'var(--ink-faint)', textAlign: i === 8 || i === 9 ? 'right' : undefined }}>{h}</span>
            ))}
          </div>
          {productos.map((p, idx) => {
            const isDragging = draggingId === p.id;
            const isHover = hoverId === p.id && draggingId !== null && draggingId !== p.id;
            return (
              <div
                key={p.id}
                draggable
                onDragStart={(e) => {
                  setDraggingId(p.id);
                  e.dataTransfer.effectAllowed = 'move';
                  // En Firefox hace falta setData para que se inicie el drag
                  e.dataTransfer.setData('text/plain', String(p.id));
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setHoverId(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (draggingId !== null && draggingId !== p.id) setHoverId(p.id);
                }}
                onDragLeave={() => {
                  if (hoverId === p.id) setHoverId(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggingId !== null && draggingId !== p.id) {
                    moverProducto(draggingId, p.id);
                  }
                  setDraggingId(null);
                  setHoverId(null);
                }}
                className="grid gap-1 px-2 py-1.5 rounded items-center transition-all"
                style={{
                  gridTemplateColumns: GRID_COLS,
                  background: isHover ? 'var(--surface-raised)' : 'var(--surface-deep)',
                  border: isHover
                    ? '1px dashed var(--emp-accent)'
                    : '1px solid var(--line-dim)',
                  opacity: isDragging ? 0.4 : 1,
                  cursor: 'grab',
                  transform: isHover ? 'translateY(2px)' : 'none',
                }}
                title="Arrastra para reordenar"
              >
                <GripVertical size={12} style={{ color: 'var(--ink-faint)' }} />
                <span className="font-mono text-[10px] tabular-nums" style={{ color: 'var(--ink-faint)' }}>
                  {idx + 1}
                </span>
                <span className="font-mono text-[11px] font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{p.articulo}</span>
                <div className="min-w-0">
                  <p className="text-[11px] font-medium truncate" style={{ color: 'var(--ink)' }}>{p.nombre_producto}</p>
                  {p.descripcion && <p className="text-[10px] truncate" style={{ color: 'var(--ink-faint)' }}>{p.descripcion}</p>}
                </div>
                <span className="text-[10px] truncate" style={{ color: 'var(--ink-faint)' }}>{p.departamento ?? '—'}</span>
                {(() => {
                  const volMal = !!aguaCiclo && (p.volumen == null || Number(p.volumen) !== aguaCiclo);
                  return (
                    <span className="text-[11px] font-mono text-right"
                      style={{ color: volMal ? 'var(--tone-warn-text)' : 'var(--ink)' }}
                      title={volMal
                        ? `Volumen de referencia ${p.volumen == null ? 'sin definir' : Number(p.volumen).toLocaleString('es-CR')} ≠ agua del ciclo ${aguaCiclo!.toLocaleString('es-CR')} lts`
                        : undefined}>
                      {p.dosis.toLocaleString('es-CR', { minimumFractionDigits: 3 })}{volMal ? ' *' : ''}
                    </span>
                  );
                })()}
                <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{p.medida ?? '—'}</span>
                <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{p.reingreso ?? 'N/A'}</span>
                {(() => {
                  const c = costos.get(p.id);
                  const sinCosto = !c || c.sin_costo;
                  return (
                    <>
                      <span className="text-[10px] font-mono text-right tabular-nums"
                        style={{ color: sinCosto ? 'var(--tone-warn-text)' : 'var(--ink-muted)' }}
                        title={sinCosto ? 'Sin costo en inventario (falta ingreso)' : undefined}>
                        {sinCosto ? 'sin costo' : fmtCRC(c!.costo_unitario)}
                      </span>
                      <span className="text-[11px] font-mono text-right tabular-nums font-semibold"
                        style={{ color: sinCosto ? 'var(--tone-warn-text)' : 'var(--ink)' }}>
                        {sinCosto ? '—' : fmtCRC(c!.costo_ha)}
                      </span>
                    </>
                  );
                })()}
                <div className="flex gap-0.5">
                  <button onClick={() => setModal(p)} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                    <Pencil size={11} />
                  </button>
                  <button onClick={() => setDeleting(p)} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
          {/* Total del ciclo por hectárea */}
          <div className="grid gap-1 px-2 py-2 mt-1 rounded items-center"
            style={{ gridTemplateColumns: GRID_COLS, borderTop: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
            <span /><span /><span />
            <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
              Total ciclo / ha
            </span>
            <span /><span /><span /><span /><span />
            <span className="text-[12px] font-mono text-right tabular-nums font-bold" style={{ color: 'var(--emp-accent-txt)' }}>
              {fmtCRC(totalCicloHa)}
            </span>
            <span />
          </div>
          {nSinCosto > 0 && (
            <p className="text-[10px] mt-1 px-2 flex items-center gap-1" style={{ color: 'var(--tone-warn-text)' }}>
              <AlertCircle size={11} /> {nSinCosto} producto{nSinCosto > 1 ? 's' : ''} sin costo en inventario (falta ingreso) — no suma{nSinCosto > 1 ? 'n' : ''} al total.
            </p>
          )}
          {/* Hint de uso */}
          {productos.length >= 2 && (
            <p className="text-[10px] mt-2 px-2 italic" style={{ color: 'var(--ink-faint)' }}>
              Arrastra ⋮⋮ para reordenar. El orden se respeta al generar boletas.
            </p>
          )}
        </div>
      )}

      {modal !== null && (
        <Modal title={modal === 'create' ? `Nuevo Producto — ${ciclo.nombre}` : `Editar — ${(modal as AppPlanProducto).nombre_producto}`}
          onClose={() => setModal(null)} wide>
          <ProductoForm cicloId={ciclo.id} empresaId={empresaId} aguaCiclo={aguaCiclo ?? undefined}
            initial={modal !== 'create' ? (modal as AppPlanProducto) : undefined}
            onSave={save} onCancel={() => setModal(null)} />
        </Modal>
      )}


      {deleting && (
        <Modal title="Quitar Producto" onClose={() => setDeleting(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Quitar <strong>{deleting.nombre_producto}</strong> del ciclo <strong>{ciclo.nombre}</strong>?
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleting(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDelete}>Quitar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export function PlanManejoPage() {
  const empresaId = useEmpresaId();

  // Booms
  const [booms, setBooms] = useState<AppBoom[]>([]);
  const [modalBoom, setModalBoom] = useState<'create' | AppBoom | null>(null);
  const [showBooms, setShowBooms] = useState(false);

  // Etapas
  const [etapas, setEtapas] = useState<AppPlanEtapa[]>([]);
  const [etapaSeleccionada, setEtapaSeleccionada] = useState<AppPlanEtapa | null>(null);
  const [modalEtapa, setModalEtapa] = useState<'create' | AppPlanEtapa | null>(null);
  const [deleteEtapa, setDeleteEtapa] = useState<AppPlanEtapa | null>(null);

  // Ciclos
  const [ciclos, setCiclos] = useState<AppPlanCiclo[]>([]);
  const [cicloSeleccionado, setCicloSeleccionado] = useState<AppPlanCiclo | null>(null);
  const [modalCiclo, setModalCiclo] = useState<'create' | AppPlanCiclo | null>(null);
  const [deleteCiclo, setDeleteCiclo] = useState<AppPlanCiclo | null>(null);

  const [loading, setLoading] = useState(true);

  const [soloActivas, setSoloActivas] = useState(true);

  // ── Carga inicial ──────────────────────────────────────────────────────────

  const cargarBooms = useCallback(async () => {
    const { data } = await supabase.from('app_booms').select('*')
      .eq('empresa_id', empresaId).eq('activo', true).order('codigo');
    setBooms(data ?? []);
  }, [empresaId]);

  const cargarEtapas = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('app_plan_etapas').select('*')
      .eq('empresa_id', empresaId).order('orden').order('nombre');
    if (soloActivas) q = q.eq('activo', true);
    const { data } = await q;
    setEtapas(data ?? []);
    setLoading(false);
  }, [empresaId, soloActivas]);

  const cargarCiclos = useCallback(async (etapaId: number) => {
    const { data } = await supabase.from('app_plan_ciclos').select('*, boom:app_booms(*)')
      .eq('empresa_id', empresaId).eq('etapa_id', etapaId)
      .order('dias');
    const lista = data ?? [];
    setCiclos(lista);
    // Mantener seleccionado el mismo ciclo (con sus datos frescos) tras editarlo;
    // si cambió la etapa, el id ya no está en la lista y se limpia.
    setCicloSeleccionado(prev => (prev ? lista.find(c => c.id === prev.id) ?? null : null));
  }, [empresaId]);

  useEffect(() => {
    cargarBooms();
    cargarEtapas();
  }, [cargarBooms, cargarEtapas]);

  useEffect(() => {
    if (etapaSeleccionada) cargarCiclos(etapaSeleccionada.id);
    else { setCiclos([]); setCicloSeleccionado(null); }
  }, [etapaSeleccionada, cargarCiclos]);

  // ── CRUD Booms ─────────────────────────────────────────────────────────────

  async function saveBoom(data: Partial<AppBoom>) {
    if (modalBoom && modalBoom !== 'create') {
      await supabase.from('app_booms').update(data).eq('id', (modalBoom as AppBoom).id);
    } else {
      await supabase.from('app_booms').insert({ ...data, empresa_id: empresaId });
    }
    setModalBoom(null);
    await cargarBooms();
  }

  // ── CRUD Etapas ────────────────────────────────────────────────────────────

  async function saveEtapa(data: Partial<AppPlanEtapa>) {
    if (modalEtapa && modalEtapa !== 'create') {
      await supabase.from('app_plan_etapas').update(data).eq('id', (modalEtapa as AppPlanEtapa).id);
    } else {
      await supabase.from('app_plan_etapas').insert({ ...data, empresa_id: empresaId });
    }
    setModalEtapa(null);
    await cargarEtapas();
  }

  async function confirmDeleteEtapa() {
    if (!deleteEtapa) return;
    await supabase.from('app_plan_etapas').update({ activo: false }).eq('id', deleteEtapa.id);
    if (etapaSeleccionada?.id === deleteEtapa.id) setEtapaSeleccionada(null);
    setDeleteEtapa(null);
    await cargarEtapas();
  }

  async function activarEtapa(etapa: AppPlanEtapa) {
    await supabase.from('app_plan_etapas').update({ activo: true }).eq('id', etapa.id);
    await cargarEtapas();
    setEtapaSeleccionada(current => current?.id === etapa.id ? { ...current, activo: true } : current);
  }

  // ── CRUD Ciclos ────────────────────────────────────────────────────────────

  async function saveCiclo(data: Partial<AppPlanCiclo>) {
    if (!etapaSeleccionada) return;
    if (modalCiclo && modalCiclo !== 'create') {
      const anterior = modalCiclo as AppPlanCiclo;
      await supabase.from('app_plan_ciclos').update(data).eq('id', anterior.id);
      // El agua por boom es el volumen de referencia de la fórmula: la cantidad
      // por boom es (dosis / volumen) × capacidad. Si cambia el agua y los
      // productos se quedan con el volumen viejo, la suma de los booms deja de
      // ser dosis × área. Propagar a todo el ciclo.
      const aguaNueva = data.agua_l;
      if (aguaNueva != null && Number(aguaNueva) !== Number(anterior.agua_l ?? NaN)) {
        await supabase.from('app_plan_productos')
          .update({ volumen: Number(aguaNueva) })
          .eq('empresa_id', empresaId)
          .eq('ciclo_id', anterior.id);
      }
    } else {
      await supabase.from('app_plan_ciclos').insert({ ...data, empresa_id: empresaId, etapa_id: etapaSeleccionada.id });
    }
    setModalCiclo(null);
    await cargarCiclos(etapaSeleccionada.id);
  }

  async function confirmDeleteCiclo() {
    if (!deleteCiclo || !etapaSeleccionada) return;
    await supabase.from('app_plan_ciclos').update({ activo: false }).eq('id', deleteCiclo.id);
    if (cicloSeleccionado?.id === deleteCiclo.id) setCicloSeleccionado(null);
    setDeleteCiclo(null);
    await cargarCiclos(etapaSeleccionada.id);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 shrink-0 flex items-center justify-between gap-4"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
        <div>
          <h1 className={pageTitleCls}>Plan de Manejo</h1>
          <p className={pageSubtitleCls}>Fórmulas de aplicación por etapa y ciclo · MD2 Golden</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer text-xs" style={{ color: 'var(--ink-muted)' }}>
            <input type="checkbox" checked={soloActivas} onChange={e => { setSoloActivas(e.target.checked); setEtapaSeleccionada(null); }} />
            Solo activas
          </label>
          <button
            className={btnSecondary}
            onClick={() => setShowBooms(true)}
            title="Configurar equipos Boom"
          >
            <span className="flex items-center gap-1.5"><Settings2 size={13} /> Booms</span>
          </button>
          <button className={btnPrimary} onClick={() => setModalEtapa('create')}>
            <span className="flex items-center gap-1.5"><Plus size={14} /> Nueva Etapa</span>
          </button>
        </div>
      </div>

      {/* Layout 3 paneles */}
      <div className="flex flex-1 overflow-hidden gap-0">

        {/* Panel 1: Etapas */}
        <div className="w-56 shrink-0 flex flex-col overflow-hidden"
          style={{ borderRight: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <div className="px-3 py-2.5 shrink-0 flex items-center gap-2"
            style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <Leaf size={13} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Plan de Manejo</span>
          </div>
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--emp-accent)' }} />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto py-1">
              {etapas.map(e => {
                const active = etapaSeleccionada?.id === e.id;
                return (
                  <div key={e.id} className="group flex items-center gap-1 mx-1 my-0.5 rounded transition-colors"
                    style={{
                      background: active ? 'var(--emp-accent-bg)' : 'transparent',
                      borderLeft: active ? '2px solid var(--emp-accent)' : '2px solid transparent',
                    }}>
                    <button className="flex-1 flex items-center gap-2 px-2 py-2 text-left"
                      onClick={() => setEtapaSeleccionada(e)}>
                      <div className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: e.activo ? CICLO_COLOR[e.ciclo_cosecha] : 'var(--ink-faint)' }} />
                      <span className="text-xs truncate font-medium"
                        style={{ color: active ? 'var(--emp-accent-txt)' : e.activo ? 'var(--ink)' : 'var(--ink-faint)' }}>
                        {e.nombre}
                      </span>
                      {active && <ChevronRight size={12} style={{ color: 'var(--emp-accent-txt)', marginLeft: 'auto', flexShrink: 0 }} />}
                    </button>
                    <div className="hidden group-hover:flex items-center gap-0.5 pr-1">
                      <button onClick={() => setModalEtapa(e)} className="p-1 rounded"
                        style={{ color: 'var(--ink-faint)' }}
                        onMouseEnter={ex => (ex.currentTarget.style.color = 'var(--emp-accent-txt)')}
                        onMouseLeave={ex => (ex.currentTarget.style.color = 'var(--ink-faint)')}>
                        <Pencil size={11} />
                      </button>
                      {e.activo ? (
                        <button onClick={() => setDeleteEtapa(e)} className="p-1 rounded"
                          title="Desactivar etapa"
                          style={{ color: 'var(--ink-faint)' }}
                          onMouseEnter={ex => (ex.currentTarget.style.color = '#f87171')}
                          onMouseLeave={ex => (ex.currentTarget.style.color = 'var(--ink-faint)')}>
                          <Trash2 size={11} />
                        </button>
                      ) : (
                        <button onClick={() => activarEtapa(e)} className="p-1 rounded"
                          title="Activar etapa"
                          style={{ color: 'var(--ink-faint)' }}
                          onMouseEnter={ex => (ex.currentTarget.style.color = 'var(--tone-success-text)')}
                          onMouseLeave={ex => (ex.currentTarget.style.color = 'var(--ink-faint)')}>
                          <Power size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {etapas.length === 0 && (
                <p className="text-xs text-center py-6 px-3" style={{ color: 'var(--ink-faint)' }}>
                  Sin etapas. Creá la primera.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Panel 2: Ciclos */}
        <div className="w-64 shrink-0 flex flex-col overflow-hidden"
          style={{ borderRight: '1px solid var(--line)', background: 'var(--surface-base)' }}>
          <div className="px-3 py-2.5 shrink-0 flex items-center justify-between"
            style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <div className="flex items-center gap-2">
              <Layers size={13} style={{ color: 'var(--emp-accent-txt)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>
                Ciclos de Aplicación
              </span>
            </div>
            {etapaSeleccionada && (
              <button onClick={() => setModalCiclo('create')}
                className="p-1 rounded" title="Agregar ciclo"
                style={{ color: 'var(--ink-faint)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                <Plus size={14} />
              </button>
            )}
          </div>

          {!etapaSeleccionada ? (
            <div className="flex-1 flex items-center justify-center px-4">
              <p className="text-xs text-center" style={{ color: 'var(--ink-faint)' }}>Seleccioná una etapa</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto py-1">
              {/* Cabecera tabla */}
              <div className="grid px-3 pb-1 pt-0.5" style={{ gridTemplateColumns: '3rem 1fr 3rem 2rem', borderBottom: '1px solid var(--line-dim)' }}>
                {['Id', 'Ciclo', 'Días', ''].map(h => (
                  <span key={h} className="text-[9px] font-bold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>{h}</span>
                ))}
              </div>
              {ciclos.map(c => {
                const active = cicloSeleccionado?.id === c.id;
                return (
                  <div key={c.id} className="group grid px-3 py-1.5 mx-1 my-0.5 rounded items-center transition-colors cursor-pointer"
                    style={{
                      gridTemplateColumns: '3rem 1fr 3rem 2rem',
                      background: active ? 'var(--emp-accent-bg)' : 'transparent',
                      borderLeft: active ? '2px solid var(--emp-accent)' : '2px solid transparent',
                    }}
                    onClick={() => setCicloSeleccionado(c)}>
                    <span className="font-mono text-[10px]" style={{ color: 'var(--ink-faint)' }}>{String(c.id).padStart(3, '0')}</span>
                    <span className="text-[11px] truncate font-medium"
                      style={{ color: active ? 'var(--emp-accent-txt)' : c.activo ? 'var(--ink)' : 'var(--ink-faint)' }}>
                      {c.nombre}
                    </span>
                    <span className="text-[11px] text-right font-mono" style={{ color: active ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}>
                      {c.dias}
                    </span>
                    <div className="hidden group-hover:flex items-center gap-0.5 justify-end" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setModalCiclo(c)} className="p-0.5 rounded"
                        style={{ color: 'var(--ink-faint)' }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                        <Pencil size={10} />
                      </button>
                      <button onClick={() => setDeleteCiclo(c)} className="p-0.5 rounded"
                        style={{ color: 'var(--ink-faint)' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {ciclos.length === 0 && (
                <p className="text-xs text-center py-6 px-3" style={{ color: 'var(--ink-faint)' }}>Sin ciclos en esta etapa.</p>
              )}
            </div>
          )}
        </div>

        {/* Panel 3: Productos */}
        <div className="flex-1 flex flex-col overflow-hidden p-4">
          {!cicloSeleccionado ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 opacity-40">
              <FlaskConical size={36} style={{ color: 'var(--ink-faint)' }} />
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Seleccioná un ciclo para ver sus productos</p>
            </div>
          ) : (
            <PanelProductos ciclo={cicloSeleccionado} empresaId={empresaId} />
          )}
        </div>
      </div>

      {/* ── Modales ── */}

      {/* Booms */}
      {showBooms && (
        <Modal title="Equipos de Aplicación (Booms)" onClose={() => setShowBooms(false)}>
          <div className="space-y-3 mb-4">
            {booms.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: 'var(--ink-faint)' }}>Sin booms registrados.</p>
            ) : (
              booms.map(b => (
                <div key={b.id} className="flex items-center gap-3 px-3 py-2.5 rounded"
                  style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}>
                  <span className="font-mono text-xs font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{b.codigo}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: 'var(--ink)' }}>{b.nombre}</p>
                    <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>
                      {b.capacidad_l.toLocaleString('es-CR')} litros
                      {b.ancho_m != null
                        ? ` · ancho ${Number(b.ancho_m).toLocaleString('es-CR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} m`
                        : ' · sin ancho'}
                    </p>
                  </div>
                  <button onClick={() => setModalBoom(b)} className="p-1.5 rounded"
                    style={{ color: 'var(--ink-faint)' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                    <Pencil size={13} />
                  </button>
                </div>
              ))
            )}
          </div>
          <button className={btnSecondary} onClick={() => setModalBoom('create')}>
            <span className="flex items-center gap-1.5"><Plus size={13} /> Agregar Boom</span>
          </button>
        </Modal>
      )}

      {modalBoom !== null && (
        <Modal title={modalBoom === 'create' ? 'Nuevo Boom' : `Editar — ${(modalBoom as AppBoom).nombre}`}
          onClose={() => setModalBoom(null)}>
          <BoomForm initial={modalBoom !== 'create' ? (modalBoom as AppBoom) : undefined}
            onSave={saveBoom} onCancel={() => setModalBoom(null)} />
        </Modal>
      )}

      {/* Etapas */}
      {modalEtapa !== null && (
        <Modal title={modalEtapa === 'create' ? 'Nueva Etapa' : `Editar — ${(modalEtapa as AppPlanEtapa).nombre}`}
          onClose={() => setModalEtapa(null)}>
          <EtapaForm initial={modalEtapa !== 'create' ? (modalEtapa as AppPlanEtapa) : undefined}
            onSave={saveEtapa} onCancel={() => setModalEtapa(null)} />
        </Modal>
      )}

      {deleteEtapa && (
        <Modal title="Desactivar Etapa" onClose={() => setDeleteEtapa(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Desactivar la etapa <strong>{deleteEtapa.nombre}</strong>? Los ciclos y productos quedan intactos.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleteEtapa(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDeleteEtapa}>Desactivar</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Ciclos */}
      {modalCiclo !== null && etapaSeleccionada && (
        <Modal title={modalCiclo === 'create' ? `Nuevo Ciclo — ${etapaSeleccionada.nombre}` : `Editar — ${(modalCiclo as AppPlanCiclo).nombre}`}
          onClose={() => setModalCiclo(null)}>
          <CicloForm etapaId={etapaSeleccionada.id} booms={booms}
            initial={modalCiclo !== 'create' ? (modalCiclo as AppPlanCiclo) : undefined}
            onSave={saveCiclo} onCancel={() => setModalCiclo(null)} />
        </Modal>
      )}

      {deleteCiclo && (
        <Modal title="Desactivar Ciclo" onClose={() => setDeleteCiclo(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Desactivar el ciclo <strong>{deleteCiclo.nombre}</strong> (día {deleteCiclo.dias})?
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleteCiclo(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDeleteCiclo}>Desactivar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
